import React, { useState, useEffect } from 'react';
import { X, Mail, Save } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useNotification } from '@/contexts/NotificationContext';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/firebase/config';

interface EmailPreferences {
  taskReportEnabled: boolean;
  taskReportFrequency: 'daily' | 'weekly' | 'monthly';
  taskReportDay: number; // 0-6 for weekly (0=Sunday), 1-31 for monthly
  taskReportTime: string; // HH:MM format in UTC
}

interface EmailPreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EmailPreferencesModal: React.FC<EmailPreferencesModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [preferences, setPreferences] = useState<EmailPreferences>({
    taskReportEnabled: true,
    taskReportFrequency: 'weekly',
    taskReportDay: 1, // Monday (0=Sunday, 1=Monday)
    taskReportTime: '09:00', // 9 AM UTC
  });

  useEffect(() => {
    if (isOpen && user?.id) {
      loadPreferences();
    }
  }, [isOpen, user]);

  const loadPreferences = async () => {
    if (!user?.id) return;

    setIsLoading(true);
    try {
      const userDoc = await getDoc(doc(db, 'users', user.id));
      if (userDoc.exists()) {
        const data = userDoc.data();
        const emailPrefs = data.emailPreferences as EmailPreferences | undefined;
        
        // Default values for backward compatibility: weekly on Monday at 9 AM UTC
        const defaultPrefs: EmailPreferences = {
          taskReportEnabled: true,
          taskReportFrequency: 'weekly',
          taskReportDay: 1, // Monday
          taskReportTime: '09:00',
        };

        if (emailPrefs) {
          // User has custom preferences, use them
          setPreferences({
            taskReportEnabled: emailPrefs.taskReportEnabled ?? true,
            taskReportFrequency: emailPrefs.taskReportFrequency || 'weekly',
            taskReportDay: emailPrefs.taskReportDay ?? 1,
            taskReportTime: emailPrefs.taskReportTime || '09:00',
          });
        } else {
          // No preferences set, use defaults (backward compatibility)
          setPreferences(defaultPrefs);
        }
      } else {
        // User document doesn't exist, use defaults
        setPreferences({
          taskReportEnabled: true,
          taskReportFrequency: 'weekly',
          taskReportDay: 1,
          taskReportTime: '09:00',
        });
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
      showNotification('Failed to load preferences', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const savePreferences = async () => {
    if (!user?.id) {
      showNotification('You must be logged in to save preferences', 'error');
      return;
    }

    setIsSaving(true);
    try {
      const userRef = doc(db, 'users', user.id);
      await updateDoc(userRef, {
        emailPreferences: preferences,
        updatedAt: serverTimestamp(),
      });

      showNotification('Email preferences saved successfully!', 'success');
      onClose();
    } catch (error) {
      console.error('Error saving preferences:', error);
      showNotification('Failed to save preferences', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const weekDays = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
  ];

  const monthDays = Array.from({ length: 31 }, (_, i) => i + 1);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">Email Preferences</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                <p className="text-sm text-gray-600">Loading preferences...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <label className="text-sm font-medium text-gray-900">
                    Receive Compliance Task Reports
                  </label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Get email summaries of your pending and overdue compliance tasks
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.taskReportEnabled}
                    onChange={(e) =>
                      setPreferences({ ...preferences, taskReportEnabled: e.target.checked })
                    }
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {preferences.taskReportEnabled && (
                <>
                  {/* Frequency Selector */}
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Email Frequency
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {(['daily', 'weekly', 'monthly'] as const).map((freq) => (
                        <button
                          key={freq}
                          type="button"
                          onClick={() => {
                            setPreferences({
                              ...preferences,
                              taskReportFrequency: freq,
                              // Reset day when changing frequency
                              taskReportDay: freq === 'weekly' ? 1 : freq === 'monthly' ? 1 : 0,
                            });
                          }}
                          className={`px-3 py-2 text-sm rounded-lg border-2 transition-all ${
                            preferences.taskReportFrequency === freq
                              ? 'border-blue-600 bg-blue-50 text-blue-900 font-medium'
                              : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          {freq.charAt(0).toUpperCase() + freq.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Day/Date Selector */}
                  {preferences.taskReportFrequency !== 'daily' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        {preferences.taskReportFrequency === 'weekly'
                          ? 'Day of Week'
                          : 'Day of Month'}
                      </label>
                      <select
                        value={preferences.taskReportDay}
                        onChange={(e) =>
                          setPreferences({
                            ...preferences,
                            taskReportDay: parseInt(e.target.value, 10),
                          })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        {preferences.taskReportFrequency === 'weekly'
                          ? weekDays.map((day) => (
                              <option key={day.value} value={day.value}>
                                {day.label}
                              </option>
                            ))
                          : monthDays.map((day) => (
                              <option key={day} value={day}>
                                {day}
                                {day === 1 || day === 21 || day === 31
                                  ? 'st'
                                  : day === 2 || day === 22
                                  ? 'nd'
                                  : day === 3 || day === 23
                                  ? 'rd'
                                  : 'th'}
                              </option>
                            ))}
                      </select>
                    </div>
                  )}

                  {/* Time Selector */}
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Time (UTC)
                    </label>
                    <input
                      type="time"
                      value={preferences.taskReportTime}
                      onChange={(e) =>
                        setPreferences({ ...preferences, taskReportTime: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Emails will be sent at this time in UTC timezone
                    </p>
                  </div>

                  {/* Summary */}
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-xs text-blue-900">
                      <strong>Summary:</strong> You will receive compliance task reports{' '}
                      {preferences.taskReportFrequency === 'daily'
                        ? 'every day'
                        : preferences.taskReportFrequency === 'weekly'
                        ? `every ${weekDays.find((d) => d.value === preferences.taskReportDay)?.label}`
                        : `on the ${preferences.taskReportDay}${
                            preferences.taskReportDay === 1 || preferences.taskReportDay === 21 || preferences.taskReportDay === 31
                              ? 'st'
                              : preferences.taskReportDay === 2 || preferences.taskReportDay === 22
                              ? 'nd'
                              : preferences.taskReportDay === 3 || preferences.taskReportDay === 23
                              ? 'rd'
                              : 'th'
                          } of each month`}{' '}
                      at {preferences.taskReportTime} UTC.
                    </p>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={savePreferences}
            disabled={isSaving || isLoading}
            className="flex items-center gap-2 px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Save Preferences</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailPreferencesModal;

